import { Injectable, signal } from '@angular/core';

const MUTE_KEY = 'ecommerce.chat.mute';

/**
 * Short notification beep for admin chat.
 * Browsers block AudioContext until a user gesture — call unlock() on first click.
 */
@Injectable({ providedIn: 'root' })
export class ChatSoundService {
  readonly mute = signal(localStorage.getItem(MUTE_KEY) === '1');

  private ctx: AudioContext | null = null;
  private unlocked = false;
  private unlockBound = false;

  /** Attach a one-time gesture listener so later beeps are allowed. */
  armUnlock(): void {
    if (this.unlockBound || typeof document === 'undefined') {
      return;
    }
    this.unlockBound = true;
    const unlock = () => {
      void this.unlock();
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('pointerdown', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    try {
      const ctx = this.ensureContext();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      this.unlocked = ctx.state === 'running';
    } catch {
      this.unlocked = false;
    }
  }

  setMute(muted: boolean): void {
    this.mute.set(muted);
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    if (!muted) {
      void this.unlock();
    }
  }

  play(): void {
    if (this.mute()) {
      return;
    }

    void this.playInternal();
  }

  private async playInternal(): Promise<void> {
    try {
      const ctx = this.ensureContext();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      if (ctx.state !== 'running') {
        return;
      }

      const now = ctx.currentTime;
      this.tone(ctx, now, 880, 0.12);
      this.tone(ctx, now + 0.14, 1174, 0.16);
      this.unlocked = true;
    } catch {
      /* ignore */
    }
  }

  private tone(ctx: AudioContext, start: number, freq: number, duration: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration + 0.02);
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx();
    }
    return this.ctx;
  }
}
